/*
Governance Service

This service handles all API calls for the Governance module:
- Frameworks and their versions
- Policies (linked to frameworks) with maker-checker workflow
- Processes (SOPs) and their versions
- Procedures (linked to processes) with maker-checker workflow

Following established patterns from PROJECT_CONVENTIONS.md:
- Uses axios with withCredentials for session authentication
- Standardized response format conversion
- Error handling with proper status codes
*/

import apiClient from './apiService';

// Use shared apiClient
const apiService = apiClient;

/**
 * Extract a human-readable message from an axios error.
 * Handles FastAPI 422 validation responses (`detail` is an array of
 * `{loc, msg, type}`) plus backend `{error: "..."}` / `{message: "..."}` shapes.
 */
function extractApiError(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const parts = data.detail.map((d: any) => {
        const field = Array.isArray(d.loc) ? d.loc.filter((p: any) => p !== 'body').join('.') : '';
        const msg = d.msg || 'invalid value';
        return field ? `${field}: ${msg}` : msg;
      });
      return parts.join('; ');
    }
    if (typeof data.detail === 'string') return data.detail;
  }
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  return error?.message || fallback;
}

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface GovernanceFramework {
  id: number;
  entity_id: number;
  framework_code: string;
  framework_name: string;
  framework_type?: string;
  description?: string;
  applicability_date?: string;
  parent_framework_id?: number;
  is_global: boolean;
  global_ref_id?: number;
  applicable_law_id?: number;
  is_active: number;
  is_approved: number;
  // Real maker-checker approval gate (is_approved is a stale legacy flag not set
  // by the approve transition). Gate framework pickers on this.
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Aliases used by frontend component (mapped at component level)
  framework_description?: string;
  start_date?: string;
  end_date?: string;
  display_order?: number;
  // System-generated code (e.g. "FRM-1") — null for drafts, set on submit
  code?: string | null;
}

export interface ApplicableLaw {
  id: number;
  entity_id: number;
  law_name: string;
  regulator_name: string;
  jurisdiction?: string;
  law_type?: string;
  is_active: number;
}

export interface GovernanceFrameworkVersion {
  id: number;
  framework_id: number;
  version_number: string;
  version_status: 'in_progress' | 'released';
  version_notes?: string;
  release_date?: string;
  released_by?: number;
  is_current: boolean;
  created_by: number;
  created_on: string;
}

export interface GovernancePolicy {
  id: number;
  policy_code: string;
  policy_name: string;
  policy_description?: string;
  framework_id: number;
  framework_name?: string;
  version_number: string;
  workflow_status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  maker_id: number;
  maker_name?: string;
  made_on: string;
  checker_id?: number;
  checker_name?: string;
  checked_on?: string;
  approval_notes?: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  display_order: number;
  // System-generated code (e.g. "POL-1") — null for drafts, set on submit
  code?: string | null;
}

export interface GovernanceProcess {
  id: number;
  process_code: string;
  process_name: string;
  // Backend column is `description` (GOV_processes / ProcessBase). Keeping the
  // field name aligned with the API so the SOP modal's Description binds and
  // persists correctly (was `process_description`, which the API silently drops).
  description?: string;
  process_type: string;
  is_global: boolean;
  global_process_id?: number;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  display_order: number;
  // Review frequency in months (GOV_processes.review_cycle_months; ProcessBase
  // default 12). Surfaced in the SOP modal so a reviewer can set how often the
  // SOP must be re-reviewed; round-trips on create/update/edit.
  review_cycle_months?: number;
  // Derived flags: server-derived from ADT_procedure_templates linked back to
  // this SOP. Toggling either creates / inactivates an audit procedure row of
  // type WalkthroughDesign / WalkthroughCompliance. Audit Procedures is the
  // single source of truth — these are not separate columns on GOV_processes.
  enable_test_of_design?: boolean;
  enable_test_of_compliance?: boolean;
  // Legacy status column ('DRAFT' | 'APPROVED' | 'RETIRED') — drives the STATUS
  // badge only. NOT the approval gate: can be 'APPROVED' on rows never run through
  // maker-checker. Use workflow_state for dropdown gating.
  process_status?: string;
  // Maker-checker workflow state (migration 128) — the real approval gate the
  // traffic-light dot reads. Gate SOP dropdowns on this (mirrors /processes/dropdown).
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  // SOP versioning — the live version number, and the open draft of the next
  // version if there is one (list query only).
  current_version_number?: number;
  draft_version_number?: number | null;
  draft_workflow_state?: 'draft' | 'submitted' | 'rejected' | null;
}

export interface GovernanceProcessVersion {
  id: number;
  process_id: number;
  version_number: string;
  version_status: 'in_progress' | 'released';
  version_notes?: string;
  release_date?: string;
  released_by?: number;
  is_current: boolean;
  created_by: number;
  created_on: string;
}

export interface GovernanceProcedure {
  id: number;
  procedure_code: string;
  procedure_name: string;
  procedure_description?: string;
  process_id: number;
  process_name?: string;
  version_id: number;  // Foreign key to governance_process_versions
  version_number: string;  // Display version (e.g., "1.0", "2.1") - from joined query
  workflow_status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  maker_id: number;
  maker_name?: string;
  made_on: string;
  checker_id?: number;
  checker_name?: string;
  checked_on?: string;
  approval_notes?: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  display_order: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface QueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  framework_id?: number;
  process_id?: number;
  workflow_status?: string;
  limit?: number;
}

// ============================================
// FRAMEWORKS SERVICE
// ============================================

class GovernanceFrameworksService {
  /**
   * Get all frameworks with optional filtering
   */
  async getAll(params?: QueryParams): Promise<GovernanceFramework[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.limit) queryParams.append('limit', String(params.limit));

      const queryString = queryParams.toString();
      const url = `/api/v1/gov/frameworks${queryString ? '?' + queryString : ''}`;
      
      const response = await apiService.get<ApiResponse<GovernanceFramework[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching frameworks:', error);
      throw new Error(extractApiError(error, 'Failed to fetch frameworks'));
    }
  }

  /**
   * Get framework by ID
   */
  async getById(id: number): Promise<GovernanceFramework> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceFramework>>(
        `/api/v1/gov/frameworks/${id}`
      );
      if (!response.data.data) {
        throw new Error('Framework not found');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error fetching framework ${id}:`, error);
      throw new Error(extractApiError(error, 'Failed to fetch framework'));
    }
  }

  /**
   * Create new framework
   */
  async create(framework: Partial<GovernanceFramework>): Promise<GovernanceFramework> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceFramework>>(
        '/api/v1/gov/frameworks',
        framework
      );
      if (!response.data.data) {
        throw new Error('Failed to create framework');
      }
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating framework:', error);
      throw new Error(extractApiError(error, 'Failed to create framework'));
    }
  }

  /**
   * Update framework
   */
  async update(id: number, framework: Partial<GovernanceFramework>): Promise<GovernanceFramework> {
    try {
      const response = await apiService.put<ApiResponse<GovernanceFramework>>(
        `/api/v1/gov/frameworks/${id}`,
        framework
      );
      if (!response.data.data) {
        throw new Error('Failed to update framework');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating framework ${id}:`, error);
      throw new Error(extractApiError(error, 'Failed to update framework'));
    }
  }

  /**
   * Delete framework (soft delete)
   */
  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`/api/v1/gov/frameworks/${id}`);
    } catch (error: any) {
      console.error(`Error deleting framework ${id}:`, error);
      throw new Error(extractApiError(error, 'Failed to delete framework'));
    }
  }

  /**
   * Get versions for a framework
   */
  async getVersions(frameworkId: number): Promise<GovernanceFrameworkVersion[]> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceFrameworkVersion[]>>(
        `/api/v1/gov/frameworks/${frameworkId}/versions`
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error(`Error fetching versions for framework ${frameworkId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch versions');
    }
  }
}

// ============================================
// POLICIES SERVICE
// ============================================

class GovernancePoliciesService {
  /**
   * Get all policies with optional filtering
   */
  async getAll(params?: QueryParams): Promise<GovernancePolicy[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.framework_id) queryParams.append('framework_id', params.framework_id.toString());
      if (params?.workflow_status) queryParams.append('workflow_status', params.workflow_status);

      const queryString = queryParams.toString();
      const url = `/api/v1/gov/policies${queryString ? '?' + queryString : ''}`;
      
      const response = await apiService.get<ApiResponse<GovernancePolicy[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching policies:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch policies');
    }
  }

  /**
   * Get policy by ID
   */
  async getById(id: number): Promise<GovernancePolicy> {
    try {
      const response = await apiService.get<ApiResponse<GovernancePolicy>>(
        `/api/v1/gov/policies/${id}`
      );
      if (!response.data.data) {
        throw new Error('Policy not found');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error fetching policy ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch policy');
    }
  }

  /**
   * Create new policy
   */
  async create(policy: Partial<GovernancePolicy>): Promise<GovernancePolicy> {
    try {
      const response = await apiService.post<ApiResponse<GovernancePolicy>>(
        '/api/v1/gov/policies',
        policy
      );
      if (!response.data.data) {
        throw new Error('Failed to create policy');
      }
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating policy:', error);
      throw new Error(error.response?.data?.error || 'Failed to create policy');
    }
  }

  /**
   * Update policy
   */
  async update(id: number, policy: Partial<GovernancePolicy>): Promise<GovernancePolicy> {
    try {
      const response = await apiService.put<ApiResponse<GovernancePolicy>>(
        `/api/v1/gov/policies/${id}`,
        policy
      );
      if (!response.data.data) {
        throw new Error('Failed to update policy');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating policy ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to update policy');
    }
  }

  /**
   * Delete policy
   */
  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`/api/v1/gov/policies/${id}`);
    } catch (error: any) {
      console.error(`Error deleting policy ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to delete policy');
    }
  }

  /**
   * Submit policy for approval
   */
  async submitForApproval(id: number): Promise<GovernancePolicy> {
    try {
      const response = await apiService.post<ApiResponse<GovernancePolicy>>(
        `/api/v1/gov/policies/${id}/submit`
      );
      if (!response.data.data) {
        throw new Error('Failed to submit policy');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error submitting policy ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to submit policy');
    }
  }

  /**
   * Approve or reject policy
   */
  async reviewPolicy(id: number, action: 'approve' | 'reject', notes?: string): Promise<GovernancePolicy> {
    try {
      const response = await apiService.post<ApiResponse<GovernancePolicy>>(
        `/api/v1/gov/policies/${id}/${action}`,
        { approval_notes: notes }
      );
      if (!response.data.data) {
        throw new Error(`Failed to ${action} policy`);
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error ${action}ing policy ${id}:`, error);
      throw new Error(error.response?.data?.error || `Failed to ${action} policy`);
    }
  }

  /**
   * Reopen an approved/rejected policy back to draft (unlocks editing).
   */
  async reopen(id: number): Promise<GovernancePolicy> {
    try {
      const response = await apiService.post<ApiResponse<GovernancePolicy>>(
        `/api/v1/gov/policies/${id}/reopen`
      );
      if (!response.data.data) {
        throw new Error('Failed to reopen policy');
      }
      return response.data.data;
    } catch (error: any) {
      console.error(`Error reopening policy ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to reopen policy');
    }
  }
}

// ============================================
// PROCESSES (SOPs) SERVICE
// ============================================

class GovernanceProcessesService {
  private basePath = '/api/v1/gov/processes';

  async getAll(params?: QueryParams): Promise<GovernanceProcess[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);

      const qs = queryParams.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response = await apiService.get<ApiResponse<GovernanceProcess[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching processes:', error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw new Error(error.response?.data?.error || 'Failed to fetch processes');
    }
  }

  async getById(id: number): Promise<GovernanceProcess> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceProcess>>(`${this.basePath}/${id}`);
      if (!response.data.data) throw new Error('Process not found');
      return response.data.data;
    } catch (error: any) {
      console.error(`Error fetching process ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch process');
    }
  }

  async create(process: Partial<GovernanceProcess>): Promise<GovernanceProcess> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceProcess>>(this.basePath, process);
      if (!response.data.data) throw new Error('Failed to create process');
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating process:', error);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create process');
    }
  }

  async update(id: number, process: Partial<GovernanceProcess>): Promise<GovernanceProcess> {
    try {
      const response = await apiService.put<ApiResponse<GovernanceProcess>>(`${this.basePath}/${id}`, process);
      if (!response.data.data) throw new Error('Failed to update process');
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating process ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to update process');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`${this.basePath}/${id}`);
    } catch (error: any) {
      console.error(`Error deleting process ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to delete process');
    }
  }
}

// ============================================
// PROCEDURES SERVICE
// ============================================

class GovernanceProceduresService {
  private basePath = '/api/v1/gov/procedures';

  async getAll(params?: QueryParams): Promise<GovernanceProcedure[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.process_id) queryParams.append('process_id', params.process_id.toString());
      if (params?.workflow_status) queryParams.append('workflow_status', params.workflow_status);

      const qs = queryParams.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response = await apiService.get<ApiResponse<GovernanceProcedure[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching procedures:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch procedures');
    }
  }

  async getById(id: number): Promise<GovernanceProcedure> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceProcedure>>(`${this.basePath}/${id}`);
      if (!response.data.data) throw new Error('Procedure not found');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch procedure');
    }
  }

  async create(procedure: Partial<GovernanceProcedure>): Promise<GovernanceProcedure> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceProcedure>>(this.basePath, procedure);
      if (!response.data.data) throw new Error('Failed to create procedure');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create procedure');
    }
  }

  async update(id: number, procedure: Partial<GovernanceProcedure>): Promise<GovernanceProcedure> {
    try {
      const response = await apiService.put<ApiResponse<GovernanceProcedure>>(`${this.basePath}/${id}`, procedure);
      if (!response.data.data) throw new Error('Failed to update procedure');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update procedure');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`${this.basePath}/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete procedure');
    }
  }

  async submitForApproval(id: number): Promise<GovernanceProcedure> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceProcedure>>(`${this.basePath}/${id}/submit`);
      if (!response.data.data) throw new Error('Failed to submit procedure');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to submit procedure');
    }
  }

  async reviewProcedure(id: number, action: 'approve' | 'reject', notes?: string): Promise<GovernanceProcedure> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceProcedure>>(
        `${this.basePath}/${id}/${action}`,
        { approval_notes: notes }
      );
      if (!response.data.data) throw new Error(`Failed to ${action} procedure`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || `Failed to ${action} procedure`);
    }
  }
}

// ============================================
// PROCESS STEPS SERVICE (SOP Procedure Steps)
// ============================================

export type StepType = 'PERFORM' | 'APPROVE' | 'REVIEW' | 'NOTIFY' | 'OTHER';
export type TimelineAnchor = 'AFTER_PREV_END' | 'AFTER_PREV_START' | 'FIXED';

export type EngagementNature = 'Responsible' | 'Accountable' | 'Consulted' | 'Informed' | 'Implementer';

export const ENGAGEMENT_NATURES: EngagementNature[] = [
  'Responsible', 'Accountable', 'Consulted', 'Informed', 'Implementer',
];

// Migration 123: one step can have multiple authorities, each with a role.
export interface StepAuthority {
  id: number;
  step_id: number;
  authority_id: number;
  authority_name?: string | null;
  role: StepType;
  is_active?: number;
}

// Migration 124: links a step to audit procedure templates (HOW to test it).
export interface StepAuditLink {
  id: number;
  process_step_id: number;
  procedure_template_id: number;
  procedure_name?: string | null;
  procedure_type?: string | null;
  authority_id?: number | null;
  authority_name?: string | null;
  is_active?: number;
}

export interface ProcessStep {
  id: number;
  entity_id: number;
  process_id: number;
  step_number: number;
  step_name: string;
  description?: string;
  step_type?: StepType;
  after_step_id?: number | null;
  after_step_name?: string | null;
  timeline_days?: number | null;
  timeline_anchor?: TimelineAnchor;
  /** Authorities engaged on this step — populated by _attach_authorities. */
  authorities?: StepAuthority[];
  /** Audit procedure links — populated by _attach_audit_links. */
  audit_links?: StepAuditLink[];
  /** Draft steps only: the live step this draft step edits (null = added in the draft). */
  live_step_id?: number | null;
  draft_id?: number;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

/** @deprecated use StepAuthority. Kept as alias for old callers. */
export interface StepStakeholder {
  id: number;
  authority_id: number;
  authority_name?: string | null;
  engagement_nature: EngagementNature;
}

class GovernanceProcessStepsService {
  async getByProcess(processId: number): Promise<ProcessStep[]> {
    try {
      const response = await apiService.get<ApiResponse<ProcessStep[]>>(`/api/v1/gov/processes/${processId}/steps`);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching process steps:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch process steps');
    }
  }

  async create(step: Partial<ProcessStep>): Promise<ProcessStep> {
    try {
      const response = await apiService.post<ApiResponse<ProcessStep>>('/api/v1/gov/process-steps', step);
      if (!response.data.data) throw new Error('Failed to create step');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create step');
    }
  }

  async update(id: number, step: Partial<ProcessStep>): Promise<ProcessStep> {
    try {
      const response = await apiService.put<ApiResponse<ProcessStep>>(`/api/v1/gov/process-steps/${id}`, step);
      if (!response.data.data) throw new Error('Failed to update step');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update step');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`/api/v1/gov/process-steps/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete step');
    }
  }

  async getContext(id: number): Promise<StepContext> {
    const r = await apiService.get<ApiResponse<StepContext>>(`/api/v1/gov/process-steps/${id}/context`);
    return r.data.data ?? { step: null, controls: [], implementations: [], risks: [] };
  }

  async reorder(processId: number, order: Array<{ step_id: number; step_number: number }>): Promise<ProcessStep[]> {
    const r = await apiService.patch<ApiResponse<ProcessStep[]>>(
      `/api/v1/gov/processes/${processId}/steps/reorder`,
      { process_id: processId, order },
    );
    return r.data.data ?? [];
  }
}

// ============================================
// SOP VERSIONING — Live / Draft / Versions
// ============================================
// A live (approved) SOP is locked. Its next version is edited as a draft that
// has its own approval workflow (WF kind "gov_sop_draft"); approving the draft
// makes it the live version. Every version that has been live is kept.

export const SOP_DRAFT_WF_KIND = 'gov_sop_draft';

export interface SopDraft {
  id: number;
  process_id: number;
  target_version_number: number;
  process_name: string;
  description?: string | null;
  owner_authority_id?: number | null;
  owner_authority_name?: string | null;
  effective_date?: string | null;
  review_cycle_months?: number | null;
  change_summary?: string | null;
  workflow_state: 'draft' | 'submitted' | 'rejected' | 'approved';
  rejection_reason?: string | null;
  created_by?: number;
  steps: ProcessStep[];
}

export interface SopDraftStepInput {
  step_name?: string;
  description?: string | null;
  step_type?: string;
  after_step_id?: number | null;
  timeline_days?: number | null;
  timeline_anchor?: string;
  is_active?: number;
  authorities?: Array<{ authority_id: number; role: string }>;
}

export interface SopChange {
  kind: 'header' | 'step';
  change: 'added' | 'edited' | 'removed';
  label: string;
  fields: Array<{ field: string; before: any; after: any }>;
}

export interface SopVersionRow {
  id: number;
  version_number: number;
  process_name: string;
  change_summary?: string | null;
  rolled_out_on: string;
  superseded_on?: string | null;
  approval_note?: string | null;
  step_count: number;
  rolled_out_by_name?: string | null;
  approved_by_name?: string | null;
  drafted_by_name?: string | null;
  is_live: boolean;
}

export interface SopSnapshotStep {
  step_id: number | null;
  step_number: number;
  step_name: string;
  description?: string | null;
  step_type?: string;
  after_step_name?: string | null;
  timeline_days?: number | null;
  timeline_anchor?: string;
  is_active: number;
  authorities: Array<{ authority_id: number; authority_name: string; role: string }>;
}

export interface SopVersionDetail extends SopVersionRow {
  snapshot: {
    header: {
      process_name: string;
      description?: string | null;
      owner_authority_name?: string | null;
      effective_date?: string | null;
      review_cycle_months?: number | null;
    };
    steps: SopSnapshotStep[];
  };
  changes: SopChange[] | null;
}

class SopVersioningService {
  async getDraft(processId: number): Promise<{ draft: SopDraft | null; liveVersion: number }> {
    const r = await apiService.get<any>(`/api/v1/gov/processes/${processId}/draft`);
    return { draft: r.data.data ?? null, liveVersion: r.data.live_version_number ?? 1 };
  }

  async openDraft(processId: number, changeSummary?: string): Promise<SopDraft> {
    const r = await apiService.post<ApiResponse<SopDraft>>(
      `/api/v1/gov/processes/${processId}/draft`,
      { change_summary: changeSummary || null },
    );
    return r.data.data as SopDraft;
  }

  async updateDraft(draftId: number, data: Partial<SopDraft>): Promise<SopDraft> {
    const r = await apiService.put<ApiResponse<SopDraft>>(`/api/v1/gov/process-drafts/${draftId}`, data);
    return r.data.data as SopDraft;
  }

  async discardDraft(draftId: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/process-drafts/${draftId}`);
  }

  async getDraftSteps(draftId: number): Promise<ProcessStep[]> {
    const r = await apiService.get<ApiResponse<ProcessStep[]>>(`/api/v1/gov/process-drafts/${draftId}/steps`);
    return r.data.data ?? [];
  }

  async createDraftStep(draftId: number, step: SopDraftStepInput): Promise<ProcessStep> {
    const r = await apiService.post<ApiResponse<ProcessStep>>(`/api/v1/gov/process-drafts/${draftId}/steps`, step);
    return r.data.data as ProcessStep;
  }

  async updateDraftStep(draftId: number, stepId: number, step: SopDraftStepInput): Promise<ProcessStep> {
    const r = await apiService.put<ApiResponse<ProcessStep>>(
      `/api/v1/gov/process-drafts/${draftId}/steps/${stepId}`, step,
    );
    return r.data.data as ProcessStep;
  }

  async deleteDraftStep(draftId: number, stepId: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/process-drafts/${draftId}/steps/${stepId}`);
  }

  async reorderDraftSteps(draftId: number, order: Array<{ step_id: number; step_number: number }>): Promise<ProcessStep[]> {
    const r = await apiService.patch<ApiResponse<ProcessStep[]>>(
      `/api/v1/gov/process-drafts/${draftId}/steps/reorder`, { order },
    );
    return r.data.data ?? [];
  }

  async getDraftChanges(draftId: number): Promise<SopChange[]> {
    const r = await apiService.get<ApiResponse<SopChange[]>>(`/api/v1/gov/process-drafts/${draftId}/changes`);
    return r.data.data ?? [];
  }

  async listVersions(processId: number): Promise<SopVersionRow[]> {
    const r = await apiService.get<ApiResponse<SopVersionRow[]>>(`/api/v1/gov/processes/${processId}/versions`);
    return r.data.data ?? [];
  }

  async getVersion(processId: number, versionNumber: number): Promise<SopVersionDetail> {
    const r = await apiService.get<ApiResponse<SopVersionDetail>>(
      `/api/v1/gov/processes/${processId}/versions/${versionNumber}`,
    );
    return r.data.data as SopVersionDetail;
  }
}

export interface StepContextControl {
  id: number;
  control_code: string;
  control_name: string;
  control_type?: string | null;
  category_id?: number | null;
}

export interface StepContextImplementation {
  id: number;
  control_id: number;
  implementation_step: string;
  implementation_description?: string | null;
  implementation_type?: string | null;
  compliance_id?: number | null;
  application_id?: number | null;
  hardware_id?: number | null;
}

export interface StepContextRisk {
  id: number;
  risk_code: string;
  risk_name: string;
  category_id?: number | null;
  control_id: number;
}

export interface StepContext {
  step: { id: number; step_number: number; step_name: string; description?: string | null; step_type?: string | null } | null;
  controls: StepContextControl[];
  implementations: StepContextImplementation[];
  risks: StepContextRisk[];
}

// Step-authority & SOP-stakeholder service

export interface SopStakeholder {
  id: number;
  process_id: number;
  authority_id: number;
  authority_name?: string | null;
  engagement_nature: EngagementNature;
  cascade_to_descendants: boolean;
  is_ack_mandatory: boolean;
  is_active: number;
}

// Derived stakeholder roll-up — read-only view of who is involved in an SOP.
export interface DerivedStakeholderStep {
    step_id: number;
    step_number: number;
    step_name: string;
    step_type: StepType;  // mirrors the authority's role on the step
}

export interface DerivedStakeholderPosting {
    position_id: number;
    location_name: string;
    posting_id?: number | null;
    user_id?: number | null;
    user_name?: string | null;
    email?: string | null;
    phone?: string | null;
}

export interface DerivedStakeholderAuthority {
    authority_id: number;
    authority_name: string;
    engagements: StepType[];
    steps: DerivedStakeholderStep[];
    postings: DerivedStakeholderPosting[];
    /** Subset of postings that resolve to a real user with an email. */
    recipients: Array<{
        user_id: number;
        user_name: string;
        email: string;
        via_authority_id: number;
        via_authority_name: string;
        via_location_name: string;
    }>;
}

export interface DerivedStakeholdersResponse {
    process_id: number;
    process_name: string;
    authorities: DerivedStakeholderAuthority[];
    /** Distinct (by user_id, then email) flattened across all authorities. */
    recipients: Array<{
        user_id: number;
        user_name: string;
        email: string;
        via_authority_id: number;
        via_authority_name: string;
        via_location_name: string;
    }>;
}

export interface MySopItem {
  process_id: number;
  process_name: string;
  description?: string;
  current_version_number: number;
  workflow_status: string;
  classification: 'actionable' | 'awareness';
  engagement_natures: EngagementNature[];
  via_authorities: string[];
}

// Reverse roll-up — every SOP a given authority is a stakeholder in, with the
// exact step engagements. Criss-cross of DerivedStakeholdersResponse (one SOP →
// many authorities) flipped to (one authority → many SOPs).
export interface AuthoritySopEngagement {
  step_id: number;
  step_number: number;
  step_name: string;
  role: StepType;
}

export interface AuthoritySop {
  process_id: number;
  process_code?: string | null;
  process_name: string;
  process_status: string;
  // Maker-checker state — real approval gate (matches the ApprovalDot).
  workflow_state?: string;
  version?: string | null;
  roles: StepType[];
  step_count: number;
  engagements: AuthoritySopEngagement[];
}

class GovernanceStakeholdersService {
  // ---- Step authorities (migration 123: many-to-many with explicit role) ----
  async listStepAuthorities(stepId: number): Promise<StepAuthority[]> {
    const r = await apiService.get<ApiResponse<StepAuthority[]>>(`/api/v1/gov/process-steps/${stepId}/authorities`);
    return r.data.data || [];
  }
  async addStepAuthority(payload: { step_id: number; authority_id: number; role: StepType }): Promise<StepAuthority> {
    const r = await apiService.post<ApiResponse<StepAuthority>>('/api/v1/gov/step-authorities', payload);
    if (!r.data.data) throw new Error('Failed to add step authority');
    return r.data.data;
  }
  async updateStepAuthority(id: number, payload: { role: StepType }): Promise<StepAuthority> {
    const r = await apiService.put<ApiResponse<StepAuthority>>(`/api/v1/gov/step-authorities/${id}`, payload);
    if (!r.data.data) throw new Error('Failed to update step authority');
    return r.data.data;
  }
  async removeStepAuthority(id: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/step-authorities/${id}`);
  }

  // ---- Step audit links (migration 124: HOW to test each step) ----
  async listStepAuditLinks(stepId: number): Promise<StepAuditLink[]> {
    const r = await apiService.get<ApiResponse<StepAuditLink[]>>(`/api/v1/gov/process-steps/${stepId}/audit-links`);
    return r.data.data || [];
  }
  async addStepAuditLink(payload: { process_step_id: number; procedure_template_id: number; authority_id?: number | null }): Promise<StepAuditLink> {
    const r = await apiService.post<ApiResponse<StepAuditLink>>('/api/v1/gov/process-step-audit-links', payload);
    if (!r.data.data) throw new Error('Failed to add audit link');
    return r.data.data;
  }
  async updateStepAuditLink(id: number, payload: { authority_id: number | null }): Promise<StepAuditLink> {
    const r = await apiService.put<ApiResponse<StepAuditLink>>(`/api/v1/gov/process-step-audit-links/${id}`, payload);
    if (!r.data.data) throw new Error('Failed to update audit link');
    return r.data.data;
  }
  async removeStepAuditLink(id: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/process-step-audit-links/${id}`);
  }

  async listSopStakeholders(processId: number): Promise<SopStakeholder[]> {
    const r = await apiService.get<ApiResponse<SopStakeholder[]>>(`/api/v1/gov/processes/${processId}/stakeholders`);
    return r.data.data || [];
  }
  async addSopStakeholder(payload: {
    process_id: number; authority_id: number; engagement_nature: EngagementNature;
    cascade_to_descendants?: boolean; is_ack_mandatory?: boolean;
  }): Promise<SopStakeholder> {
    const r = await apiService.post<ApiResponse<SopStakeholder>>('/api/v1/gov/sop-stakeholders', payload);
    if (!r.data.data) throw new Error('Failed to add SOP stakeholder');
    return r.data.data;
  }
  async updateSopStakeholder(id: number, payload: Partial<SopStakeholder>): Promise<SopStakeholder> {
    const r = await apiService.put<ApiResponse<SopStakeholder>>(`/api/v1/gov/sop-stakeholders/${id}`, payload);
    if (!r.data.data) throw new Error('Failed to update SOP stakeholder');
    return r.data.data;
  }
  async removeSopStakeholder(id: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/sop-stakeholders/${id}`);
  }

  /** Read-only stakeholder roll-up for an SOP. Authorities are derived from
      the step.authority_id set; each authority is fanned out to its postings
      so the caller can preview the mailer recipient list. No materialization
      anywhere — the response is computed per request. */
  async getDerived(processId: number): Promise<DerivedStakeholdersResponse> {
    const r = await apiService.get<ApiResponse<DerivedStakeholdersResponse>>(
      `/api/v1/gov/processes/${processId}/stakeholders-derived`,
    );
    return r.data.data ?? { process_id: processId, process_name: "", authorities: [], recipients: [] };
  }

  async getMySops(): Promise<MySopItem[]> {
    const r = await apiService.get<ApiResponse<MySopItem[]>>('/api/v1/gov/my-sops');
    return r.data.data || [];
  }

  /** Reverse of getDerived: every SOP this authority is a stakeholder in, each
      grouped with the step(s) + role of the engagement. All process statuses;
      the endpoint returns a raw array (no success wrapper). */
  async getAuthoritySops(authorityId: number): Promise<AuthoritySop[]> {
    const r = await apiService.get<AuthoritySop[]>(`/api/v1/gov/authorities/${authorityId}/sops`);
    const d = r.data as unknown;
    if (Array.isArray(d)) return d as AuthoritySop[];
    return ((d as ApiResponse<AuthoritySop[]>)?.data) ?? [];
  }
}

export const governanceStakeholdersService = new GovernanceStakeholdersService();

// ============================================
// CONTRACTS SERVICE
// ============================================

export interface GovernanceContract {
  id: number;
  entity_id: number;
  contract_code?: string;
  // System-generated code (CNT-N) — null until approved.
  code?: string | null;
  code_seq?: number | null;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  title: string;
  party_name: string;
  contract_type: string;
  contract_status: string;
  start_date?: string;
  end_date?: string;
  contract_value?: string;
  owner?: string;
  description?: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

export interface GovernanceContractTerm {
  id: number;
  entity_id: number;
  contract_id: number;
  clause_reference?: string;
  title: string;
  description?: string;
  criticality: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

export interface ContractQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
}

class GovernanceContractsService {
  private basePath = '/api/v1/gov/contracts';

  async getAll(params?: ContractQueryParams): Promise<GovernanceContract[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.type && params.type !== 'ALL') queryParams.append('type', params.type);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const qs = queryParams.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response = await apiService.get<ApiResponse<GovernanceContract[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching contracts:', error);
      if (error.response?.status === 401) window.location.href = '/login';
      throw new Error(error.response?.data?.error || 'Failed to fetch contracts');
    }
  }

  async getById(id: number): Promise<GovernanceContract> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceContract>>(`${this.basePath}/${id}`);
      if (!response.data.data) throw new Error('Contract not found');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch contract');
    }
  }

  async create(data: Partial<GovernanceContract>): Promise<GovernanceContract> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceContract>>(this.basePath, data);
      if (!response.data.data) throw new Error('Failed to create contract');
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating contract:', error);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create contract');
    }
  }

  async update(id: number, data: Partial<GovernanceContract>): Promise<GovernanceContract> {
    try {
      const response = await apiService.put<ApiResponse<GovernanceContract>>(`${this.basePath}/${id}`, data);
      if (!response.data.data) throw new Error('Failed to update contract');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update contract');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`${this.basePath}/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete contract');
    }
  }
}

class GovernanceContractTermsService {
  async getByContract(contractId: number): Promise<GovernanceContractTerm[]> {
    try {
      const response = await apiService.get<ApiResponse<GovernanceContractTerm[]>>(`/api/v1/gov/contracts/${contractId}/terms`);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching contract terms:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch contract terms');
    }
  }

  async create(data: Partial<GovernanceContractTerm>): Promise<GovernanceContractTerm> {
    try {
      const response = await apiService.post<ApiResponse<GovernanceContractTerm>>('/api/v1/gov/contract-terms', data);
      if (!response.data.data) throw new Error('Failed to create term');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create term');
    }
  }

  async update(id: number, data: Partial<GovernanceContractTerm>): Promise<GovernanceContractTerm> {
    try {
      const response = await apiService.put<ApiResponse<GovernanceContractTerm>>(`/api/v1/gov/contract-terms/${id}`, data);
      if (!response.data.data) throw new Error('Failed to update term');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update term');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await apiService.delete(`/api/v1/gov/contract-terms/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete term');
    }
  }
}

// ============================================
// POLICY-PROCEDURE LINKS SERVICE
// ============================================

export interface PolicyProcedureLink {
  id: number;
  entity_id: number;
  sop_id: number;
  sop_code?: string;
  sop_name?: string;
  sop_description?: string;
  notes?: string;
  is_active: number;
  // legacy compat for any code still reading step_* — kept optional
  process_id?: number;
  process_name?: string;
  step_number?: number;
  step_name?: string;
}

export interface AvailableProcedure {
  id: number;
  process_code?: string;
  process_name: string;
  description?: string;
  // legacy compat
  process_step_id?: number;
  step_number?: number;
  step_name?: string;
}

class PolicyProcedureLinksService {
  private basePath = '/api/v1/gov';

  async getLinksForPolicy(policyId: number): Promise<PolicyProcedureLink[]> {
    try {
      const response = await apiService.get<ApiResponse<PolicyProcedureLink[]>>(
        `${this.basePath}/policies/${policyId}/sops`
      );
      const rows = response.data.data || [];
      // surface SOP head fields with backward-compatible alias fields for UI
      return rows.map((r: any) => ({
        ...r,
        process_id: r.sop_id,
        process_name: r.sop_name,
        step_name: r.sop_name,
      }));
    } catch (error: any) {
      console.error('Error fetching policy SOP links:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch links');
    }
  }

  async getAvailableProcedures(policyId: number, _search?: string): Promise<AvailableProcedure[]> {
    try {
      const url = `${this.basePath}/policies/${policyId}/sops/available`;
      const response = await apiService.get<ApiResponse<AvailableProcedure[]>>(url);
      const rows = response.data.data || [];
      // backend doesn't filter by search server-side; do client-side
      const filtered = _search
        ? rows.filter((r: any) =>
            (r.process_name || '').toLowerCase().includes(_search.toLowerCase()) ||
            (r.process_code || '').toLowerCase().includes(_search.toLowerCase()),
          )
        : rows;
      return filtered.map((r: any) => ({
        ...r,
        process_step_id: r.id,
        step_name: r.process_name,
      }));
    } catch (error: any) {
      console.error('Error fetching available SOPs:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch available SOPs');
    }
  }

  async createLink(data: { policy_id: number; process_step_id?: number; sop_id?: number; link_type?: string; notes?: string }): Promise<PolicyProcedureLink> {
    try {
      const sopId = data.sop_id ?? data.process_step_id;
      if (!sopId) throw new Error('sop_id required');
      const response = await apiService.post<ApiResponse<PolicyProcedureLink>>(
        `${this.basePath}/policies/${data.policy_id}/sops`,
        { sop_id: sopId, notes: data.notes },
      );
      if (!response.data.data) throw new Error('Failed to create link');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create link');
    }
  }

  // Legacy signature accepted (linkId from old API). New backend uses (policyId, sopId).
  async removeLink(policyId: number, sopId: number): Promise<void> {
    try {
      await apiService.delete(`${this.basePath}/policies/${policyId}/sops/${sopId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to remove link');
    }
  }
}

// ============================================
// TERM-SOP LINKS SERVICE (contract terms ↔ SOP heads)
// ============================================

export interface TermSopLink {
  id: number;
  entity_id: number;
  sop_id: number;
  sop_code?: string;
  sop_name?: string;
  sop_description?: string;
  notes?: string;
  is_active: number;
}

export interface TermAvailableSop {
  id: number;
  process_code?: string;
  process_name: string;
  description?: string;
}

class TermSopLinksService {
  private basePath = '/api/v1/gov';

  async getLinksForTerm(termId: number): Promise<TermSopLink[]> {
    try {
      const response = await apiService.get<ApiResponse<TermSopLink[]>>(
        `${this.basePath}/terms/${termId}/sops`,
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching term SOP links:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch links');
    }
  }

  async getAvailableSops(termId: number, search?: string): Promise<TermAvailableSop[]> {
    try {
      const response = await apiService.get<ApiResponse<TermAvailableSop[]>>(
        `${this.basePath}/terms/${termId}/sops/available`,
      );
      const rows = response.data.data || [];
      return search
        ? rows.filter((r: any) =>
            (r.process_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (r.process_code || '').toLowerCase().includes(search.toLowerCase()),
          )
        : rows;
    } catch (error: any) {
      console.error('Error fetching available SOPs for term:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch available SOPs');
    }
  }

  async createLink(termId: number, sopId: number, notes?: string): Promise<TermSopLink> {
    try {
      const response = await apiService.post<ApiResponse<TermSopLink>>(
        `${this.basePath}/terms/${termId}/sops`,
        { sop_id: sopId, notes },
      );
      if (!response.data.data) throw new Error('Failed to create link');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create link');
    }
  }

  async removeLink(termId: number, sopId: number): Promise<void> {
    try {
      await apiService.delete(`${this.basePath}/terms/${termId}/sops/${sopId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to remove link');
    }
  }
}

// ============================================
// MEETINGS SERVICE
// ============================================

export type MeetingVoteModel = 'BINARY' | 'TRINARY';
export type MeetingVoteThreshold =
  | 'SIMPLE_MAJORITY'
  | 'MAJORITY_WITH_QUORUM'
  | 'SUPERMAJORITY'
  | 'CHAIRMAN_VETO';

export interface GovernanceMeeting {
  id: number;
  entity_id: number;
  meeting_title: string;
  meeting_code: string;
  meeting_description?: string;
  meeting_type: string;
  chairman_id: number;
  chairman_name?: string;
  scheduled_date: string;
  scheduled_time_from?: string;
  scheduled_time_to?: string;
  location?: string;
  meeting_link?: string | null;
  vote_model: MeetingVoteModel;
  vote_threshold: MeetingVoteThreshold;
  organizer_id?: number;
  meeting_status: string;
  minutes_text?: string;
  closed_by?: number;
  closed_on?: string;
  // Manual (user-written) minutes — editable by chair/attendees until the chair
  // finalizes (migration 159). Distinct from the auto-generated minutes_text.
  manual_minutes_text?: string;
  manual_minutes_finalized?: boolean;
  manual_minutes_finalized_by?: number;
  manual_minutes_finalized_on?: string;
  is_active: number;
  is_approved: number;
  // System code (MTG-N) — assigned on approve; null until then.
  code?: string | null;
  code_seq?: number | null;
  // Maker-checker workflow (the approval dot) — distinct from meeting_status.
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by_name?: string;
  submitted_on?: string;
  approved_by_name?: string;
  approved_on?: string;
  approval_note?: string;
  rejected_by_name?: string;
  rejected_on?: string;
  rejection_reason?: string;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // v3 (Attend flow): when this meeting was returned from /meetings/mine,
  // the backend joins the current user's participant row so the staff portal
  // can render the Attend button / attendance pill without an extra fetch.
  my_has_attended?: boolean;
  my_attended_from_time?: string;
}

export interface MeetingQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  meeting_status?: string;
  meeting_type?: string;
  page?: number;
  limit?: number;
}

class GovernanceMeetingsService {
  private basePath = '/api/v1/gov/meetings';

  // --- Maker-checker workflow transitions (the approval dot) ---
  async submit(id: number): Promise<void> {
    await apiService.patch(`${this.basePath}/${id}/submit`);
  }
  async approve(id: number, note?: string): Promise<void> {
    await apiService.patch(`${this.basePath}/${id}/approve`, { note });
  }
  async reject(id: number, reason: string): Promise<void> {
    await apiService.patch(`${this.basePath}/${id}/reject`, { reason });
  }
  async reopen(id: number): Promise<void> {
    await apiService.patch(`${this.basePath}/${id}/reopen`);
  }
  async getHistory(id: number): Promise<Array<{ audit_id: number; action: string; changed_by_username: string; changed_on: string; change_reason: string }>> {
    const response = await apiService.get(`${this.basePath}/${id}/history`);
    const rows = response.data?.data ?? [];
    return (rows as any[]).map(r => ({
      audit_id: r.id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }

  async getAll(params?: MeetingQueryParams): Promise<GovernanceMeeting[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'ALL') queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.meeting_status) queryParams.append('meeting_status', params.meeting_status);
      if (params?.meeting_type) queryParams.append('meeting_type', params.meeting_type);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const qs = queryParams.toString();
      const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
      const response = await apiService.get<ApiResponse<GovernanceMeeting[]>>(url);
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching meetings:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch meetings');
    }
  }

  async getById(id: number): Promise<GovernanceMeeting> {
    const response = await apiService.get<ApiResponse<GovernanceMeeting>>(`${this.basePath}/${id}`);
    if (!response.data.data) throw new Error('Meeting not found');
    return response.data.data;
  }

  async create(data: Partial<GovernanceMeeting>): Promise<GovernanceMeeting> {
    const response = await apiService.post<ApiResponse<GovernanceMeeting>>(this.basePath, data);
    if (!response.data.data) throw new Error('Failed to create meeting');
    return response.data.data;
  }

  async update(id: number, data: Partial<GovernanceMeeting>): Promise<GovernanceMeeting> {
    const response = await apiService.put<ApiResponse<GovernanceMeeting>>(`${this.basePath}/${id}`, data);
    if (!response.data.data) throw new Error('Failed to update meeting');
    return response.data.data;
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`${this.basePath}/${id}`);
  }

  async getMine(upcomingOnly = false): Promise<(GovernanceMeeting & { my_role: 'Chairman' | 'Participant'; chairman_name?: string })[]> {
    const url = `${this.basePath}/mine${upcomingOnly ? '?upcoming_only=true' : ''}`;
    const response = await apiService.get<ApiResponse<any[]>>(url);
    return response.data.data || [];
  }

  /** v3: explicit attend gate. Server flips has_attended=TRUE (idempotent),
   * stamps attended_from_time on the first call, and transitions
   * DRAFT/SCHEDULED -> IN_PROGRESS. Voting buttons remain disabled until
   * this resolves successfully. */
  async attend(meetingId: number): Promise<{ has_attended: boolean; attended_from_time?: string }> {
    const response = await apiService.post<ApiResponse<{ has_attended: boolean; attended_from_time?: string }>>(
      `${this.basePath}/${meetingId}/attend`,
    );
    return response.data.data || { has_attended: true };
  }
}

// ============================================
// MEETING AGENDA ITEMS SERVICE
// ============================================

export type AgendaStatus = 'PENDING' | 'PASSED' | 'NOT_PASSED';

export interface MeetingAgendaItem {
  id: number;
  code?: string | null;
  entity_id: number;
  meeting_id: number;
  template_agenda_id?: number;
  agenda_name: string;
  agenda_description?: string;
  agenda_type?: string;
  presenter?: string;
  presenter_id?: number | null;
  duration_minutes?: number;
  display_order?: number;
  is_discussed?: boolean;
  is_resolved?: boolean;
  resolution_text?: string;
  votes_for?: number;
  votes_against?: number;
  votes_abstain?: number;
  // BINARY-model tallies (T9.5). For TRINARY meetings these will be 0.
  votes_upvote?: number;
  votes_downvote?: number;
  // Server-computed pass/fail per vote_model + vote_threshold (T3 helper).
  // Always populated on GET responses.
  agenda_status?: AgendaStatus;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
}

// Transactional create payload (T3/T9.4): the POST endpoint now accepts an
// optional links[] array so the agenda row + its initial link set are
// persisted in a single transaction (SPEC R8).
export interface MeetingAgendaItemCreate extends Partial<MeetingAgendaItem> {
  links?: Array<{
    item_type_id: number;
    item_record_id: number;
    relationship_type?: string;
    notes?: string;
  }>;
}

class MeetingAgendaService {
  async getByMeeting(meetingId: number): Promise<MeetingAgendaItem[]> {
    const response = await apiService.get<ApiResponse<MeetingAgendaItem[]>>(`/api/v1/gov/meetings/${meetingId}/agenda-items`);
    return response.data.data || [];
  }

  async create(data: MeetingAgendaItemCreate): Promise<MeetingAgendaItem> {
    const response = await apiService.post<ApiResponse<MeetingAgendaItem>>('/api/v1/gov/meeting-agenda-items', data);
    if (!response.data.data) throw new Error('Failed to create agenda item');
    return response.data.data;
  }

  async update(id: number, data: Partial<MeetingAgendaItem>): Promise<MeetingAgendaItem> {
    const response = await apiService.put<ApiResponse<MeetingAgendaItem>>(`/api/v1/gov/meeting-agenda-items/${id}`, data);
    if (!response.data.data) throw new Error('Failed to update agenda item');
    return response.data.data;
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/meeting-agenda-items/${id}`);
  }
}

// ============================================
// MEETING PARTICIPANTS SERVICE
// ============================================

export interface MeetingParticipant {
  id: number;
  entity_id: number;
  meeting_id: number;
  user_id: number;
  position_id?: number;
  is_scheduled_participant?: boolean;
  has_attended?: boolean;
  attended_from_time?: string;
  attended_to_time?: string;
  is_active: number;
  user_name?: string;
  login_id?: string;
  position_name?: string;
  created_on?: string;
}

export interface AvailableUser {
  id: number;
  first_name: string;
  last_name: string;
  login_id: string;
  user_name: string;
}

class MeetingParticipantsService {
  async getByMeeting(meetingId: number): Promise<MeetingParticipant[]> {
    const response = await apiService.get<ApiResponse<MeetingParticipant[]>>(`/api/v1/gov/meetings/${meetingId}/participants`);
    return response.data.data || [];
  }

  async add(data: { meeting_id: number; user_id: number; position_id?: number }): Promise<MeetingParticipant> {
    const response = await apiService.post<ApiResponse<MeetingParticipant>>('/api/v1/gov/meeting-participants', data);
    if (!response.data.data) throw new Error('Failed to add participant');
    return response.data.data;
  }

  async remove(id: number): Promise<void> {
    await apiService.delete(`/api/v1/gov/meeting-participants/${id}`);
  }

  async getAvailableUsers(meetingId?: number, search?: string): Promise<AvailableUser[]> {
    const params = new URLSearchParams();
    if (meetingId) params.append('meeting_id', meetingId.toString());
    if (search) params.append('search', search);
    const qs = params.toString();
    const response = await apiService.get<ApiResponse<AvailableUser[]>>(`/api/v1/gov/available-users${qs ? `?${qs}` : ''}`);
    return response.data.data || [];
  }
}

// ============================================
// AGENDA VOTES SERVICE
// ============================================

// SPEC R22: BINARY vote_model accepts only UPVOTE/DOWNVOTE; TRINARY accepts
// only FOR/AGAINST/ABSTAIN. Server enforces; client passes through.
export type AgendaVoteValue =
  | 'FOR'
  | 'AGAINST'
  | 'ABSTAIN'
  | 'UPVOTE'
  | 'DOWNVOTE';

export interface AgendaVote {
  id: number;
  entity_id: number;
  meeting_id: number;
  agenda_instance_id: number;
  user_id: number;
  vote: AgendaVoteValue;
  remarks?: string;
  user_name?: string;
  created_on?: string;
}

export interface AgendaVoteCastResponse extends AgendaVote {
  // T4: POST /agenda-votes returns the updated agenda_status alongside the
  // newly persisted vote row so callers don't need a separate refresh.
  agenda_status?: AgendaStatus;
}

class AgendaVotesService {
  async getByAgenda(agendaId: number): Promise<AgendaVote[]> {
    const response = await apiService.get<ApiResponse<AgendaVote[]>>(`/api/v1/gov/meeting-agenda-items/${agendaId}/votes`);
    return response.data.data || [];
  }

  async castVote(data: {
    meeting_id: number;
    agenda_instance_id: number;
    vote: AgendaVoteValue;
    remarks?: string;
  }): Promise<AgendaVoteCastResponse> {
    const response = await apiService.post<ApiResponse<AgendaVoteCastResponse>>('/api/v1/gov/agenda-votes', data);
    if (!response.data.data) throw new Error('Failed to cast vote');
    return response.data.data;
  }
}

// ============================================
// MEETING CLOSE SERVICE
// ============================================

class MeetingCloseService {
  async closeMeeting(meetingId: number, minutesText: string): Promise<GovernanceMeeting> {
    const response = await apiService.post<ApiResponse<GovernanceMeeting>>(`/api/v1/gov/meetings/${meetingId}/close`, { minutes_text: minutesText });
    if (!response.data.data) throw new Error('Failed to close meeting');
    return response.data.data;
  }

  /**
   * Compose the auto-generated minutes (agenda items + per-person responses +
   * spawned tasks) on demand WITHOUT closing the meeting. Returns the markdown
   * so the caller can insert it into the editable draft. Chair / attended
   * participant only.
   */
  async generateAutoMinutes(meetingId: number): Promise<string> {
    const response = await apiService.get<ApiResponse<{ minutes_text: string }>>(
      `/api/v1/gov/meetings/${meetingId}/auto-minutes`,
    );
    return response.data.data?.minutes_text ?? '';
  }

  // --- Manual (user-written) minutes (migration 159) ---

  /** Save the human-written minutes. Chair or attended participant; rejected if finalized. */
  async saveManualMinutes(meetingId: number, text: string): Promise<GovernanceMeeting> {
    const response = await apiService.put<ApiResponse<GovernanceMeeting>>(
      `/api/v1/gov/meetings/${meetingId}/manual-minutes`,
      { manual_minutes_text: text },
    );
    if (!response.data.data) throw new Error('Failed to save minutes');
    return response.data.data;
  }

  /** Lock the manual minutes read-only. Chair-only; requires non-empty text. */
  async finalizeManualMinutes(meetingId: number): Promise<GovernanceMeeting> {
    const response = await apiService.post<ApiResponse<GovernanceMeeting>>(
      `/api/v1/gov/meetings/${meetingId}/manual-minutes/finalize`,
      {},
    );
    if (!response.data.data) throw new Error('Failed to finalize minutes');
    return response.data.data;
  }

  /** Reopen finalized minutes for editing. Chair-only. */
  async unfinalizeManualMinutes(meetingId: number): Promise<GovernanceMeeting> {
    const response = await apiService.post<ApiResponse<GovernanceMeeting>>(
      `/api/v1/gov/meetings/${meetingId}/manual-minutes/unfinalize`,
      {},
    );
    if (!response.data.data) throw new Error('Failed to reopen minutes');
    return response.data.data;
  }
}

// ============================================
// RELATED MEETINGS SERVICE (reverse polymorphic lookup)
// ============================================

export interface RelatedMeeting {
  meeting_id: number;
  meeting_code: string;
  meeting_title: string;
  meeting_type: string;
  meeting_status: string;
  meeting_date: string | null;
  agenda_id: number;
  agenda_name: string;
  relationship_type?: string | null;
  link_notes?: string | null;
}

class RelatedMeetingsService {
  /**
   * Reverse lookup: meetings whose agenda items link to the given entity record.
   * Backend: GET /api/v1/gov/related-meetings?item_type_code={code}&item_record_id={id}&limit=100
   */
  async getRelatedMeetings(
    itemTypeCode: string,
    itemRecordId: number | string,
    limit = 100,
  ): Promise<RelatedMeeting[]> {
    try {
      const params = new URLSearchParams();
      params.append('item_type_code', itemTypeCode);
      params.append('item_record_id', String(itemRecordId));
      params.append('limit', String(limit));
      const response = await apiService.get<ApiResponse<RelatedMeeting[]>>(
        `/api/v1/gov/related-meetings?${params.toString()}`,
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching related meetings:', error);
      throw new Error(
        error.response?.data?.error ||
          error.response?.data?.detail ||
          'Failed to fetch related meetings',
      );
    }
  }
}

// ============================================
// ============================================
// APPLICABLE LAWS SERVICE
// ============================================

class GovernanceApplicableLawsService {
  async getAll(params?: { status?: string; search?: string }): Promise<ApplicableLaw[]> {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'ALL') query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    const url = `/api/v1/gov/applicable-laws${query.toString() ? '?' + query.toString() : ''}`;
    const response = await apiService.get<ApiResponse<ApplicableLaw[]>>(url);
    return (response.data as any)?.data ?? [];
  }

  /** Approved + active applicable laws only — for select/combobox fields.
   *  /dropdown returns { items: [{ id, name }] }; map to the ApplicableLaw shape. */
  async getDropdown(): Promise<ApplicableLaw[]> {
    const response = await apiService.get<ApiResponse<any>>(
      '/api/v1/gov/applicable-laws/dropdown'
    );
    // /dropdown body = { total, eligible, items:[{id,name,code?}] } (no success/data wrapper).
    const items: any[] = (response.data as any)?.items ?? [];
    return items.map((i): ApplicableLaw => ({ id: i.id, law_name: i.name, code: i.code ?? null } as any));
  }
}

// ============================================
// EXPORT SERVICE INSTANCES
// ============================================

export const governanceApplicableLawsService = new GovernanceApplicableLawsService();
export const governanceFrameworksService = new GovernanceFrameworksService();
export const governancePoliciesService = new GovernancePoliciesService();
export const governanceProcessesService = new GovernanceProcessesService();
export const governanceProceduresService = new GovernanceProceduresService();
export const governanceProcessStepsService = new GovernanceProcessStepsService();
export const sopVersioningService = new SopVersioningService();
export const governanceContractsService = new GovernanceContractsService();
export const governanceContractTermsService = new GovernanceContractTermsService();
export const policyProcedureLinksService = new PolicyProcedureLinksService();
export const termSopLinksService = new TermSopLinksService();
export const governanceMeetingsService = new GovernanceMeetingsService();
export const meetingAgendaService = new MeetingAgendaService();
export const meetingParticipantsService = new MeetingParticipantsService();
export const agendaVotesService = new AgendaVotesService();
export const meetingCloseService = new MeetingCloseService();
export const relatedMeetingsService = new RelatedMeetingsService();

export default {
  frameworks: governanceFrameworksService,
  policies: governancePoliciesService,
  processes: governanceProcessesService,
  procedures: governanceProceduresService,
  processSteps: governanceProcessStepsService,
  contracts: governanceContractsService,
  contractTerms: governanceContractTermsService,
  policyProcedureLinks: policyProcedureLinksService,
  meetings: governanceMeetingsService,
  meetingAgenda: meetingAgendaService,
  meetingParticipants: meetingParticipantsService,
  agendaVotes: agendaVotesService,
  meetingClose: meetingCloseService,
  relatedMeetings: relatedMeetingsService
};

// ---------- Insights ----------
export interface GovCount {
  label: string;
  count: number;
}

export interface GovernanceInsightsData {
  frameworks_pending: number;
  sops_with_drafts: number;
  meetings_upcoming: number;
  tasks_open: number;
  tasks_overdue: number;
  tasks_due_week: number;
  contracts_expiring_90: number;
  contracts_expired: number;
  frameworks_by_state: GovCount[];
  frameworks_by_type: GovCount[];
  contracts_by_status: GovCount[];
  contracts_by_type: GovCount[];
  sops_by_status: GovCount[];
  meetings_by_status: GovCount[];
}

export const govInsightsApi = {
  get: async (): Promise<GovernanceInsightsData> => {
    const response = await apiService.get('/api/v1/gov/insights');
    return response.data.data;
  },
};
